import { Link } from "react-router-dom";
import { ArrowUpRight, Monitor, Globe2, Sparkles } from "lucide-react";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";

const COURSES = [
  {
    id: "corso-base",
    icon: Monitor,
    tag: "Per iniziare",
    title: "Corso Base di Informatica",
    lead: "Quello che avresti sempre voluto imparare.",
    text: "Indicato per chi non sa niente o quasi niente dell'utilizzo del computer. Il percorso crea una figura in possesso di cognizioni di informatica di base, telematica e internet, progettazione per il Web, office automation, elaborazione digitale e creazione di immagini, multimedialità e hardware. Con tempistiche e metodi che ti faranno scoprire le tue capacità, senza più timori di non farcela: lo scopo è la tua completa soddisfazione, finalmente indipendente e sicuro nell'uso del tuo PC.",
    img: "/images/corso-base-full.jpg",
    alt: "Un'allieva senior impara divertendosi in aula",
  },
  {
    id: "web-design",
    icon: Globe2,
    tag: "Specializzazione web",
    title: "Web Design",
    lead: "Il mondo della progettazione per il Web.",
    text: "Indicato per chi vuole specializzarsi nella progettazione per il Web: prototipazione e progettazione di siti web dinamici e aggiornati, tramite le più attuali tecnologie. Il percorso formativo si sofferma sull'analisi di applicativi facili e intuitivi.",
    img: "/images/web-design-full.jpg",
    alt: "Laboratorio con studenti al lavoro sui computer",
  },
  {
    id: "specializzazioni",
    icon: Sparkles,
    tag: "Argomenti avanzati",
    title: "Corsi di Specializzazione",
    lead: "Quello che ti sarebbe servito... e che potrebbe servirti domani.",
    text: "Corsi di specializzazione specifici per argomenti avanzati. Le possibilità create da una conoscenza approfondita in questi campi sono innumerevoli: scopri come imparare quello che hai sempre rimandato.",
    chips: ["AutoCAD", "Adobe Photoshop", "Animazione tridimensionale", "Corel Draw", "Grafica pubblicitaria ed editoriale", "Video editing"],
    img: "/images/spec-full.jpg",
    alt: "Lavagna: gli argomenti avanzati delle specializzazioni",
  },
];

export default function Corsi() {
  return (
    <main>
      <PageHero
        eyebrow="I corsi"
        titleLines={["Ecco i nostri corsi:", "scegli il tuo obiettivo."]}
        subtitle="Tutti i nostri corsi sono suddivisi per livelli di conoscenza e tematiche. Un unico scopo: il raggiungimento del tuo obiettivo, con la certezza del risultato."
      />

      <section className="mx-auto max-w-7xl space-y-24 px-5 py-16 pb-24 sm:px-8" data-testid="courses-list">
        {COURSES.map((c, i) => (
          <article
            key={c.id}
            data-testid={`course-${c.id}`}
            className={`grid items-center gap-12 lg:grid-cols-2 ${i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`}
          >
            <Reveal>
              <div className="relative overflow-hidden rounded-[28px] border border-[#D8E5E8] shadow-[0_20px_60px_-30px_rgba(16,51,59,0.35)]">
                <img
                  src={c.img}
                  alt={c.alt}
                  className="aspect-[4/3] w-full object-cover transition-transform duration-700 hover:scale-105"
                  loading="lazy"
                />
                <span className="absolute left-5 top-5 rounded-full border border-[#1F8296]/40 bg-white/90 px-4 py-1.5 font-plex text-[11px] uppercase tracking-[0.2em] text-[#1F8296] backdrop-blur">
                  {c.tag}
                </span>
              </div>
            </Reveal>
            <Reveal delay={0.12}>
              <div className="flex items-center gap-3">
                <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl border border-[#1F8296]/30 bg-[#1F8296]/10">
                  <c.icon className="h-5 w-5 text-[#1F8296]" />
                </div>
                <h2 className="font-display text-2xl font-semibold tracking-tight text-[#1A2E33] sm:text-3xl">{c.title}</h2>
              </div>
              <p className="mt-4 font-script text-2xl text-[#238496]">{c.lead}</p>
              <p className="mt-3 text-sm leading-relaxed text-[#5C7178] sm:text-base">{c.text}</p>
              {c.chips && (
                <div className="mt-6 flex flex-wrap gap-2.5">
                  {c.chips.map((chip) => (
                    <span key={chip} className="rounded-full border border-[#C9DBDF] bg-[#EFF6F7] px-4 py-2 text-xs font-semibold text-[#33484F]">
                      {chip}
                    </span>
                  ))}
                </div>
              )}
              <Link
                to="/contatti"
                data-testid={`course-${c.id}-cta`}
                className="group mt-8 inline-flex items-center gap-2 text-sm font-semibold text-[#1F8296] transition-colors hover:text-[#238496]"
              >
                Richiedi informazioni su questo corso
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </Reveal>
          </article>
        ))}
      </section>

      <section className="mx-auto max-w-7xl px-5 pb-28 sm:px-8" data-testid="courses-cta">
        <Reveal>
          <div className="relative overflow-hidden rounded-[32px] border border-[#D8E5E8] bg-[#EFF6F7] px-8 py-14 text-center sm:px-14">
            <div className="bg-grid-pattern pointer-events-none absolute inset-0 opacity-50" />
            <div className="relative">
              <h2 className="text-balance font-display text-3xl font-semibold tracking-tight text-[#1A2E33] sm:text-4xl">
                Chiedi a noi: valutiamo insieme il tuo livello
              </h2>
              <p className="mx-auto mt-5 max-w-2xl text-sm leading-relaxed text-[#5C7178] sm:text-base">
                Insieme valuteremo le tue conoscenze e troveremo i corsi più adatti a te. L'esperienza
                dei nostri insegnanti sarà a tua disposizione per guidarti in scelte troppo difficili
                da fare da solo. Chiedici con fiducia.
              </p>
              <Link
                to="/contatti"
                data-testid="courses-cta-contatti"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#2997AB] px-8 py-4 text-sm font-bold text-white transition-colors duration-300 hover:bg-[#238496]"
              >
                Contattaci <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
